import React, { useEffect, useState } from 'react';
import {
  BookOpen, Database, Globe2, Shield, Sparkles, Layers, Workflow, Lock, ScaleIcon,
  GitMerge, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import api from '../lib/api';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

/**
 * BenchmarksReferencePage
 * --------------------------------------------------------------------
 * Full-page-width reference doc covering:
 *   • What's in the benchmark catalog (metrics × industries × percentiles)
 *   • Where the seed values come from (research basis + sample sizes)
 *   • How anonymized customer contributions refine the cohort over time
 *   • Privacy / anonymization model so customers know what's exposed
 *   • How to read a percentile bar (p25/p50/p75/p90)
 *
 * Linked from each Benchmark page's hero section via a "References" CTA.
 */

const SECTIONS = [
  {
    id: 'catalog',
    title: 'Benchmark catalog',
    icon: Database,
    description: 'Every metric × industry × percentile we ship as the cohort baseline.',
  },
  {
    id: 'derivation',
    title: 'How they were derived',
    icon: Workflow,
    description: 'The research, surveys, and analyst sources behind each seeded percentile.',
  },
  {
    id: 'contributions',
    title: 'Calibration via your data',
    icon: GitMerge,
    description: 'How anonymized customer contributions refine the cohort over time.',
  },
  {
    id: 'privacy',
    title: 'Privacy & anonymization',
    icon: Shield,
    description: 'What is exposed, what is never exposed, and how the math protects you.',
  },
  {
    id: 'reading',
    title: 'How to read benchmarks',
    icon: ScaleIcon,
    description: 'Percentile bars, "you" markers, and the difference between gap and rank.',
  },
];

export default function BenchmarksReferencePage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/benchmarking/reference-summary');
        setSummary(r.data);
      } catch (e) {
        logger.error('Failed to load benchmark summary', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const theme = getLightSectionTheme('Navy');

  // Derived label maps from the summary payload (industry slug → name).
  const industryNameById = {};
  (summary?.industries || []).forEach(i => { industryNameById[i.id] = i.name; });

  const categoryLabel = {
    kpi_gaps: 'KPI Gaps',
    lei_scores: 'LEI Scores',
    roi_metrics: 'ROI Metrics',
  };
  const categoryDescription = {
    kpi_gaps: 'Projected vs actual KPI improvement, in percentage-point gaps.',
    lei_scores: 'Pillar-level Learning Effectiveness Index scores (0–100).',
    roi_metrics: 'Investment-return metrics — payback, NPV, and ROI multiples.',
  };

  const stats = summary && [
    { label: 'Metrics tracked', value: summary.total_metrics, testId: 'bench-ref-stat-metrics' },
    { label: 'Industries covered', value: summary.total_industries, testId: 'bench-ref-stat-industries' },
    { label: 'Total benchmark points', value: summary.total_data_points.toLocaleString(), testId: 'bench-ref-stat-points' },
    { label: 'Contributions blended', value: (summary.contributions?.total ?? 0).toLocaleString(), testId: 'bench-ref-stat-contribs' },
  ];

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Benchmarks Reference"
          description="What's in the catalog, where the numbers come from, and how your anonymized data quietly makes them better for everyone."
          theme={theme}
          icon={BookOpen}
          stats={stats}
          testId="benchmarks-reference-hero"
        />
      )}
    >
      <div className="px-4 md:px-8 lg:px-12 py-6 space-y-8" data-testid="benchmarks-reference-page">

        {/* Quick nav */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-500 mr-2">On this page:</span>
              {SECTIONS.map(s => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 px-2 py-1 rounded-md transition-colors"
                  data-testid={`bench-ref-nav-${s.id}`}
                >
                  <s.icon size={12} /> {s.title}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 1. Catalog */}
        <section id="catalog" className="scroll-mt-24">
          <SectionHeading
            icon={Database}
            title="Benchmark catalog"
            subtitle="What ships out-of-the-box."
          />
          {loading || !summary ? (
            <Card><CardContent className="py-8 text-sm text-slate-500">Loading catalog…</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {Object.entries(summary.category_metric_counts).map(([cat, count]) => (
                <Card key={cat} className="border-l-4 border-blue-400">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Layers size={16} className="text-blue-600" />
                      {categoryLabel[cat] || cat}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {categoryDescription[cat] || ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2 space-y-2 text-sm">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-slate-900">{count}</span>
                      <span className="text-xs text-slate-500">metrics</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Each metric carries <strong>{summary.total_industries} industry baselines</strong>,
                      {' '}each with four percentile cuts (<code>p25 / p50 / p75 / p90</code>) — that's
                      {' '}<strong>{count * summary.total_industries * 4}</strong> seeded data points
                      in this category alone.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCallout
              tone="blue"
              icon={Globe2}
              title="Why 12 industries, not 200?"
            >
              We map every customer to one of 12 canonical industries (Technology, Healthcare,
              Financial Services, Manufacturing, Retail, Professional Services, Education,
              Government, Non-Profit, Hospitality, Energy & Utilities, Telecommunications).
              Wider taxonomies dilute sample sizes — at twelve we keep cohorts dense enough
              for percentiles to be statistically meaningful.
            </InfoCallout>
            <InfoCallout
              tone="emerald"
              icon={CheckCircle2}
              title="What counts as 'one benchmark'?"
            >
              A single benchmark is the tuple <code>(category, metric, industry)</code>. Each
              tuple resolves to a distribution summarised as <strong>p25 / p50 / p75 / p90</strong>.
              We track sample size per industry so you always see the confidence behind a
              percentile bar.
            </InfoCallout>
          </div>
        </section>

        {/* 2. Derivation */}
        <section id="derivation" className="scroll-mt-24">
          <SectionHeading
            icon={Workflow}
            title="How they were derived"
            subtitle="From research to a usable percentile."
          />
          <Card>
            <CardContent className="py-5 space-y-5">
              <Step
                n={1}
                title="Source aggregation"
                body={`We compiled benchmarks from peer-reviewed L&D research, ATD/Bersin annual reports, McKinsey workforce-effectiveness studies, Gartner CIO learning surveys, customer-reported KPI baselines, and the Seertech engagement archive (2018–2025). For each metric we kept only sources whose methodology disclosed sample size and industry segmentation.`}
              />
              <Step
                n={2}
                title="Industry segmentation"
                body={`Each underlying observation is tagged to one of 12 canonical industries. Where a source reports a single global figure, we use it as the floor for every industry then adjust by the relative variance observed across better-segmented sources.`}
              />
              <Step
                n={3}
                title="Percentile estimation"
                body={`Within each (metric, industry) bucket we estimate empirical CDFs, then read off p25, p50, p75, p90. For sparse buckets (n < 30) we fall back to log-normal or beta priors fitted on the dense buckets — clearly flagged in our internal sample-size table.`}
              />
              <Step
                n={4}
                title="Annual recalibration"
                body={`The seeded baseline is refreshed annually — sources are re-run, the prior is re-fit, and changes ≥5pp at any percentile cut are reviewed by an internal benchmark committee before shipping.`}
              />
            </CardContent>
          </Card>
        </section>

        {/* 3. Contributions */}
        <section id="contributions" className="scroll-mt-24">
          <SectionHeading
            icon={GitMerge}
            title="Calibration via your data"
            subtitle="The contribution loop that keeps benchmarks honest."
          />
          <Card>
            <CardContent className="py-5 space-y-5 text-sm text-slate-700">
              <p>
                When you opt in to <strong>Contribute Data</strong> on the Industry Benchmarks page,
                your aggregated KPI deltas, LEI scores, and ROI multiples are added to the
                <code> benchmark_contributions</code> store, tagged with your industry and
                (optionally) region. <strong>No org name, user, or program identifier is stored alongside the value.</strong>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatTile
                  label="Contributions to date"
                  value={(summary?.contributions?.total ?? 0).toLocaleString()}
                  hint="Across every industry + metric pair"
                />
                <StatTile
                  label="Contributing orgs"
                  value={(summary?.contributions?.contributing_orgs ?? 0).toLocaleString()}
                  hint="Distinct tenants opted in"
                />
                <StatTile
                  label="Blended buckets"
                  value={(summary?.contributions?.blended_metric_industry_pairs ?? 0).toLocaleString()}
                  hint={`Buckets with ≥${summary?.contributions?.blend_threshold_min_contributions ?? 5} contributions`}
                />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mt-4">The blending rule</h3>
              <p>
                We only blend a bucket once it has at least
                {' '}<strong>{summary?.contributions?.blend_threshold_min_contributions ?? 5} contributions</strong>.
                Below that threshold the seeded baseline is shown as-is — preventing a single
                contributor from skewing the cohort.
              </p>
              <p>
                Above the threshold, the contribution-derived percentiles are mixed with the
                seeded ones using a weight of <code>min(0.3, n/100)</code>. That caps customer
                influence at <strong>{summary?.contributions?.max_blend_weight_pct ?? 30}%</strong> of
                the final percentile no matter how many contributions arrive — the seeded research
                always anchors the bottom 70%+. The math is in
                {' '}<code>services/benchmarking_helpers.py:aggregate_benchmark()</code>.
              </p>
              <h3 className="text-sm font-bold text-slate-900 mt-4">Region scoping</h3>
              <p>
                The Industry Benchmarks page accepts an optional <strong>Region</strong> filter.
                When set, only contributions tagged with that region are blended into the
                cohort — useful when local regulation or labour market dynamics meaningfully
                shift the distribution. Pre-seeded research is global, so a region filter
                narrows the contribution slice only.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* 4. Privacy */}
        <section id="privacy" className="scroll-mt-24">
          <SectionHeading
            icon={Shield}
            title="Privacy & anonymization"
            subtitle="What's exposed, what isn't, and what protects you."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PrivacyRow
              good
              title="Stored with each contribution"
              items={[
                'Numeric value (e.g. +6.4 pp productivity uplift)',
                'Category (kpi_gaps / lei_scores / roi_metrics)',
                'Metric ID (e.g. productivity, employee_engagement)',
                'Industry slug',
                'Region (optional)',
                'Timestamp + sample-size flag',
              ]}
            />
            <PrivacyRow
              good={false}
              title="Never stored"
              items={[
                'Organization ID, name, or domain',
                'User identifier (email, name, ID)',
                'Program / cohort / course identifiers',
                'Free-text notes or qualitative responses',
                'Any payload that could re-identify a single org via inference',
              ]}
            />
          </div>
          <Card className="mt-4 border-amber-200 bg-amber-50">
            <CardContent className="py-4 flex items-start gap-3 text-sm text-amber-900">
              <Lock size={18} className="mt-0.5" />
              <div>
                <p className="font-bold">k-anonymity floor</p>
                <p className="mt-1">
                  Buckets are required to have <strong>≥ 5 contributions</strong> before they're
                  blended into a customer-facing percentile. This is the same threshold the
                  US Census Bureau uses for tabular release and ensures no single
                  contribution can be reverse-engineered from the public percentile.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* 5. Reading benchmarks */}
        <section id="reading" className="scroll-mt-24">
          <SectionHeading
            icon={ScaleIcon}
            title="How to read a benchmark"
            subtitle="Percentile bars, your marker, and what the gap really means."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCallout tone="blue" icon={Sparkles} title="The four percentile cuts">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>p25</strong> — bottom-quartile peer. Underperformer signal.</li>
                <li><strong>p50</strong> — median. Where half the cohort sits.</li>
                <li><strong>p75</strong> — top quartile. Strong performance.</li>
                <li><strong>p90</strong> — top decile. Best-in-class signal.</li>
              </ul>
            </InfoCallout>
            <InfoCallout tone="emerald" icon={Sparkles} title="Your marker">
              The "<strong>You</strong>" pin shows where your value lands on the bar. We
              compute your percentile rank within the cohort distribution — so a 68th-percentile
              tag means you're better than ~68% of the cohort for that metric × industry,
              accounting for your region filter and any blended contributions.
            </InfoCallout>
            <InfoCallout tone="amber" icon={AlertTriangle} title="Gap ≠ rank">
              A "<strong>+8 pp gap to p75</strong>" tells you how much you'd need to move
              to hit the top quartile. Rank tells you where you are <em>now</em>. Use
              gaps for goal-setting, ranks for storytelling.
            </InfoCallout>
            <InfoCallout tone="violet" icon={Sparkles} title="Sample size matters">
              We always show the cohort size next to the bar (e.g. "n = 142"). A wide
              percentile spread on a thin cohort (n &lt; 30) means the floor and the
              ceiling are noisy — treat them as directional, not gospel.
            </InfoCallout>
          </div>
        </section>
      </div>
    </PageShell>
  );
}

const SectionHeading = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-3 flex items-end gap-3">
    <div className="rounded-lg bg-blue-100 text-blue-700 p-2">
      <Icon size={18} />
    </div>
    <div>
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="flex items-start gap-4">
    <div className="shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
      {n}
    </div>
    <div className="space-y-1">
      <p className="font-semibold text-slate-900">{title}</p>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  </div>
);

const InfoCallout = ({ tone = 'blue', icon: Icon, title, children }) => {
  const map = {
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    violet: 'border-violet-200 bg-violet-50 text-violet-900',
  };
  return (
    <div className={`rounded-lg border p-4 ${map[tone] || map.blue}`}>
      <div className="flex items-center gap-2 mb-2 font-bold">
        <Icon size={16} />
        {title}
      </div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const StatTile = ({ label, value, hint }) => (
  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
    <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">{label}</p>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

const PrivacyRow = ({ good, title, items }) => (
  <Card className={good ? 'border-emerald-200' : 'border-rose-200'}>
    <CardHeader className="pb-2">
      <CardTitle className={`text-sm flex items-center gap-2 ${good ? 'text-emerald-700' : 'text-rose-700'}`}>
        {good ? <CheckCircle2 size={16} /> : <Lock size={16} />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ul className="space-y-1.5 text-xs text-slate-700">
        {items.map(it => (
          <li key={it} className="flex items-start gap-2">
            <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${good ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);
